import type { DocumentView } from "@procuremate/shared";
import { Download, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DocumentStatusIcon } from "./attachments";
import { documentDownloadUrl, useRemoveDocument } from "./documents-api";

function size(bytes: number, language: string): string {
  const format = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });
  return bytes >= 1024 * 1024
    ? `${format.format(bytes / 1024 / 1024)} MB`
    : `${format.format(Math.max(1, bytes / 1024))} KB`;
}

/**
 * The request's documents in the live panel (WP-2.2): status as it is read, what it is (kind,
 * supplier, summary), download, and removal by the requester (it clears what the document supplied).
 */
export function DocumentList({
  requestId,
  documents,
  canRemove,
  removeBlocked,
}: {
  requestId: string;
  documents: DocumentView[];
  canRemove: boolean;
  removeBlocked: boolean;
}) {
  const { t, i18n } = useTranslation();
  const remove = useRemoveDocument(requestId);
  const [confirming, setConfirming] = useState<string | null>(null);
  const confirmRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  return (
    <ul className="flex flex-col divide-y divide-border">
      {documents.map((d) => (
        <li key={d.id} className="py-2.5">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 text-sm font-medium break-words text-fg">{d.fileName}</p>
            <a
              href={documentDownloadUrl(requestId, d.id)}
              className="inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg"
              aria-label={t("requests.documents.download", { name: d.fileName })}
            >
              <Download aria-hidden="true" className="h-3.5 w-3.5" />
              {t("requests.documents.downloadShort")}
            </a>
          </div>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-muted">
            <DocumentStatusIcon status={d.status} />
            {/* Shared wording with the tray ("name: reason"), so capitalised here. */}
            <span className="inline-block first-letter:uppercase">
              {d.status === "FAILED"
                ? t(`requests.documents.problems.${d.failureReason ?? "processing_failed"}`)
                : d.status === "READY"
                  ? [
                      d.kind ? t(`requests.documents.kinds.${d.kind}`) : null,
                      d.supplier,
                      d.method === "OCR" ? t("requests.documents.ocr") : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : t(`requests.documents.status.${d.status}`)}
            </span>
            <span aria-hidden="true">·</span>
            <span className="tabular">{size(d.sizeBytes, i18n.language)}</span>
          </p>
          {d.status === "READY" && d.summary ? (
            <p className="mt-1 text-sm whitespace-pre-wrap break-words text-fg">{d.summary}</p>
          ) : null}
          {canRemove ? (
            confirming === d.id ? (
              <div className="mt-2 rounded-md border border-border bg-bg p-3">
                <p ref={confirmRef} tabIndex={-1} className="text-sm text-fg outline-none">
                  {t("requests.documents.confirmRemove", { name: d.fileName })}
                </p>
                {remove.isError ? (
                  <p role="alert" className="mt-1 text-sm text-danger">
                    {t("requests.documents.removeFailed")}
                  </p>
                ) : null}
                <div className="mt-2 flex gap-2">
                  <Button
                    variant="danger"
                    className="h-9 px-3"
                    aria-disabled={remove.isPending || undefined}
                    onClick={() => {
                      if (!remove.isPending) remove.mutate(d.id, { onSuccess: () => setConfirming(null) });
                    }}
                  >
                    {t("requests.documents.remove")}
                  </Button>
                  <Button variant="secondary" className="h-9 px-3" onClick={() => setConfirming(null)}>
                    {t("requests.panel.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={removeBlocked}
                className="mt-1 inline-flex items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-danger hover:bg-danger-bg disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={t("requests.documents.removeNamed", { name: d.fileName })}
                onClick={() => {
                  remove.reset();
                  setConfirming(d.id);
                }}
              >
                <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                {t("requests.documents.remove")}
              </button>
            )
          ) : null}
        </li>
      ))}
    </ul>
  );
}
